import type {
  FieldDef,
  FormSchema,
  QuestionDef,
} from "../risikoanalyseStep/types";

/**
 * Schema for the Umwelt-Risiken step form.
 *
 * Mirrors `for-ai/umweltrisk.json`. The JSON nests yes/no follow-ups several
 * levels deep (e.g. WHG-Anlage → Kleingebinde → Einzelbehältnisse → Welche?).
 * Since QuestionDef only supports one level of `followUpFields`, deeper
 * branches are flattened into separate questions whose visibility is gated
 * with `showWhen` / `showWhenAll`.
 */

const yn = (
  question: string,
  key: string,
  followUpKey?: string,
  hint?: string,
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  ...(followUpKey
    ? {
        showFollowUpWhen: { key, equals: "ja" },
        followUpFields: [
          { key: followUpKey, label: "Kurzbeschreibung", type: "textarea" },
        ] as FieldDef[],
      }
    : {}),
});

const ynMulti = (
  question: string,
  key: string,
  followUpFields: FieldDef[],
  hint?: string,
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  showFollowUpWhen: { key, equals: "ja" },
  followUpFields,
});

const q = (question: string, field: FieldDef, hint?: string): QuestionDef => ({
  question,
  hint,
  field,
});

/**
 * Reveal a single text/textarea/select question when `gateKey` is "ja".
 * Used to model the JSON's deep follow-ups (e.g. nested "Welche?" inputs).
 */
const gated = (
  question: string,
  field: FieldDef,
  gateKey: string,
  extraGate?: { key: string; equals: string },
  hint?: string,
): QuestionDef => ({
  question,
  hint,
  field,
  showWhenAll: extraGate
    ? [{ key: gateKey, equals: "ja" }, { key: extraGate.key, equals: extraGate.equals }]
    : undefined,
  showWhen: extraGate ? undefined : { key: gateKey, equals: "ja" },
});

const GESAMTMENGE_OPTIONS = [
  { value: "bis_1000l", label: "bis 1.000 l" },
  { value: "bis_2000l", label: "bis 2.000 l" },
  { value: "bis_3000l", label: "bis 3.000 l" },
  { value: "ueber_3000l", label: "über 3.000 l" },
];

/* ── WHG-Anlage / Kleingebinde sub-questions ──────────────────────────── */
const whgKleingebindeQuestions: QuestionDef[] = [
  yn(
    "Kleingebinde und Maschineninhalte – Vorhanden? (WHG Ziffer 1.2.1)",
    "whg_kb_vorhanden",
  ),
  gated(
    "Welche Kleingebinde / Maschineninhalte?",
    { key: "whg_kb_welche", label: "Welche?", type: "textarea" },
    "whg_kb_vorhanden",
  ),
  gated(
    "Gesamtmenge",
    {
      key: "whg_kb_gesamtmenge",
      label: "Gesamtmenge",
      type: "select",
      options: GESAMTMENGE_OPTIONS,
    },
    "whg_kb_vorhanden",
  ),
  {
    question: "Gibt es Einzelbehältnisse (außer Mineralöl) über 60 l?",
    field: { key: "whg_kb_einzelbehaeltnisse", type: "yesno" },
    showWhen: { key: "whg_kb_vorhanden", equals: "ja" },
    showFollowUpWhen: { key: "whg_kb_einzelbehaeltnisse", equals: "ja" },
    followUpFields: [
      {
        key: "whg_kb_einzelbehaeltnisse_welche",
        label: "Welche?",
        type: "textarea",
      },
    ],
  },
  {
    question: "Gibt es Mineralölbehältnisse über 250 l?",
    field: { key: "whg_kb_mineraloelbehaeltnisse", type: "yesno" },
    showWhen: { key: "whg_kb_vorhanden", equals: "ja" },
    showFollowUpWhen: { key: "whg_kb_mineraloelbehaeltnisse", equals: "ja" },
    followUpFields: [
      {
        key: "whg_kb_mineraloelbehaeltnisse_welche",
        label: "Welche?",
        type: "textarea",
      },
    ],
  },
];

const whgTanksQuestions: QuestionDef[] = [
  ynMulti(
    "Tanks – Vorhanden? (WHG Ziffer 1.2.1)",
    "whg_tanks_vorhanden",
    [
      { key: "whg_tanks_art", label: "Art", type: "text" },
      { key: "whg_tanks_liter", label: "Liter", type: "number" },
      {
        key: "whg_tanks_baujahr",
        label: "Baujahr",
        type: "number",
        placeholder: "JJJJ",
      },
    ],
  ),
];

const whgTrafosQuestions: QuestionDef[] = [
  ynMulti(
    "Trafos mit Kühlöl (PCB-frei) – Vorhanden? (WHG Ziffer 1.2.1)",
    "whg_trafos_vorhanden",
    [
      { key: "whg_trafos_art", label: "Art", type: "text" },
      { key: "whg_trafos_liter", label: "Liter", type: "number" },
      {
        key: "whg_trafos_baujahr",
        label: "Baujahr",
        type: "number",
        placeholder: "JJJJ",
      },
    ],
  ),
];

/* Helper to build the recurring "vorhanden? → Art / Menge / Baujahr" block. */
const anlageBlock = (
  questionText: string,
  vorhandenKey: string,
  artKey: string,
  mengeKey: string,
  baujahrKey: string,
  mengeLabel = "Menge",
  mengeType: FieldDef["type"] = "text",
): QuestionDef =>
  ynMulti(questionText, vorhandenKey, [
    { key: artKey, label: "Art", type: "text" },
    { key: mengeKey, label: mengeLabel, type: mengeType },
    { key: baujahrKey, label: "Baujahr", type: "number", placeholder: "JJJJ" },
  ]);

export const umweltStepFormSchema: FormSchema = {
  sections: [
    {
      key: "umfeld",
      title: "Umfeld & Nachbarschaft",
      color: "pillarOrange",
      subSteps: [
        {
          key: "umfeld",
          title: "Standort, Nachbarschaft & Haftung",
          description:
            "Befindet sich das Grundstück in einem Schutzgebiet? Welche Risiken bestehen durch die Nachbarschaft?",
          questions: [
            yn(
              "Befindet sich das Grundstück oder Teile davon in einem Wasserschutz-, Wassereinzugs- oder Überschwemmungsgebiet?",
              "umw_01",
              "umw_01_beschreibung",
            ),
            q(
              "Welche Firmen befinden sich in der Nachbarschaft Ihres Betriebsgrundstücks?",
              {
                key: "umw_02",
                label: "Nachbarschaft",
                type: "textarea",
                placeholder: "z. B. Chemiewerk, ca. 200 m östlich …",
              },
              "Bitte Branche und Entfernung angeben",
            ),
            q(
              "Welche Haftungs-Summen durch Gefährdung der Nachbarschaft (z. B. übergreifende Feuerschäden) sind denkbar?",
              {
                key: "umw_03",
                label: "Geschätzte Haftungssumme",
                type: "textarea",
                placeholder: "Geschätzte Schadenhöhe in EUR …",
              },
            ),
          ],
        },
      ],
    },
    {
      key: "stoffe",
      title: "Umweltgefährdende Stoffe",
      color: "blue",
      subSteps: [
        {
          key: "stoffe_uebersicht",
          title: "Übersicht – welche Stoffe?",
          description:
            "Welche umweltgefährdenden Stoffe lagern oder verwenden Sie? Eine allgemeine Beschreibung genügt, Details folgen pro Anlagetyp.",
          questions: [
            q(
              "Welche umweltgefährdenden Stoffe lagern oder verwenden Sie?",
              {
                key: "umw_04",
                label: "Allgemeine Beschreibung",
                type: "textarea",
                placeholder: "Allgemeine Beschreibung der Stoffe …",
              },
            ),
          ],
        },
        {
          key: "stoffe_whg",
          title: "1) WHG-Anlagen",
          description:
            "Anlagen nach § 89 Abs. 2 WHG – Kleingebinde, Tanks und Trafos.",
          questions: [
            ...whgKleingebindeQuestions,
            ...whgTanksQuestions,
            ...whgTrafosQuestions,
          ],
        },
        {
          key: "stoffe_umwelthg_2a",
          title: "2a) UmweltHG-Anlagen (Anhang 1)",
          description:
            "Anlagen nach Anhang 1 des Umwelthaftungsgesetzes.",
          questions: [
            anlageBlock(
              "UmweltHG-Anlage (Anhang 1) – Vorhanden?",
              "umwelthg_2a_vorhanden",
              "umwelthg_2a_art",
              "umwelthg_2a_menge",
              "umwelthg_2a_baujahr",
            ),
          ],
        },
        {
          key: "stoffe_umwelthg_2b",
          title: "2b) UmweltHG-Anlage / Pflichtversicherung (Anhang 2)",
          description:
            "Anlagen nach Anhang 2 UmweltHG i. V. m. Störfallverordnung (12. BImSchV).",
          questions: [
            anlageBlock(
              "UmweltHG-Anlage / Pflichtversicherung (Anhang 2) – Vorhanden?",
              "umwelthg_2b_vorhanden",
              "umwelthg_2b_art",
              "umwelthg_2b_menge",
              "umwelthg_2b_baujahr",
            ),
          ],
        },
        {
          key: "stoffe_sonstige",
          title: "3) Sonstige deklarierungspflichtige Anlagen",
          description:
            "Genehmigungspflichtige Anlagen gem. 4. BImSchV (z. B. Kraftwerke, Gießereien, Heizwerke ab 50 MW), soweit nicht unter 1 oder 2 erfasst.",
          questions: [
            anlageBlock(
              "Sonstige deklarierungspflichtige Anlage – Vorhanden?",
              "sonstige_3_vorhanden",
              "sonstige_3_art",
              "sonstige_3_menge",
              "sonstige_3_baujahr",
            ),
          ],
        },
        {
          key: "stoffe_abwasser",
          title: "4) Abwasseranlagen & Einwirkungsrisiko",
          description:
            "Anlagen, die Abwasser in Vorfluter oder Kanalisation einleiten oder auf Gewässer / Grundwasser einwirken.",
          questions: [
            anlageBlock(
              "Abwasseranlage / Einwirkungsrisiko – Vorhanden?",
              "abwasser_4_vorhanden",
              "abwasser_4_art",
              "abwasser_4_menge",
              "abwasser_4_baujahr",
            ),
          ],
        },
      ],
    },
    {
      key: "taetigkeiten",
      title: "Tätigkeiten & Geothermie",
      color: "green",
      subSteps: [
        {
          key: "taetigkeiten",
          title: "Tätigkeiten & Geothermiebohrung",
          description:
            "Externe Tätigkeiten an Umwelt-Anlagen sowie Geothermiebohrungen auf eigenen Grundstücken.",
          questions: [
            ynMulti(
              "Tätigkeiten im Zusammenhang mit Umwelt-Anlagen?",
              "umw_05",
              [{ key: "umw_05_was", label: "Was genau?", type: "textarea" }],
              "Planung, Herstellung, Lieferung, Montage, Demontage, Instandhaltung und Wartung von Anlagen oder Teilen für Umwelt-Anlagen – wenn Sie nicht selbst Inhaber der Anlage sind",
            ),
            ynMulti(
              "Geothermiebohrung auf eigenen Grundstücken?",
              "umw_06",
              [
                { key: "umw_06_was", label: "Was genau?", type: "textarea" },
                {
                  key: "umw_06_wie_oft",
                  label: "Wie oft?",
                  type: "text",
                  placeholder: "z. B. einmalig, jährlich …",
                },
                {
                  key: "umw_06_zweck",
                  label: "Zu welchem Zweck?",
                  type: "textarea",
                },
                {
                  key: "umw_06_wer",
                  label: "Durch wen?",
                  type: "text",
                  placeholder: "Ausführendes Unternehmen …",
                },
              ],
              "Berg-, wasserschutz- und baurechtliche Genehmigung erforderlich. Bodengutachten und Qualifizierungsunterlagen des ausführenden Unternehmens werden benötigt.",
            ),
          ],
        },
      ],
    },
    {
      key: "eigener_boden",
      title: "Eigener Boden (USchadG)",
      color: "black",
      subSteps: [
        {
          key: "eigener_boden",
          title: "Eigener Boden – Umweltschadensgesetz",
          description:
            "Einschätzung der Risiken durch Vorfälle auf dem eigenen Grundstück.",
          questions: [
            q(
              "Halten Sie das Risiko für realistisch, dass durch einen Vorfall auf Ihrem Grundstück eine Gesundheitsgefährdung (Mitarbeitende, Kunden, Dritte) entstehen könnte?",
              { key: "umw_07a", type: "yesno" },
            ),
            q(
              "Möchten Sie auch rein ökologische Schäden am eigenen Boden absichern, selbst wenn keine Gesundheitsgefährdung vorliegt?",
              { key: "umw_07b", type: "yesno" },
            ),
          ],
        },
      ],
    },
  ],
};
